import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Badge } from "../components/ui/badge";
import { Users, Clock, CheckCircle, XCircle, Bell } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  waiting: { label: "In Queue", color: "#F4A261", icon: Clock },
  notified: { label: "Table Ready!", color: "#81B29A", icon: CheckCircle },
  seated: { label: "Seated", color: "#2F4F4F", icon: CheckCircle },
  cancelled: { label: "Cancelled", color: "#9CA3AF", icon: XCircle },
  no_show: { label: "No Show", color: "#9CA3AF", icon: XCircle },
};

export default function WaitlistStatusPage() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [swReg, setSwReg] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/waitlist/${id}/status`);
      setItem(data);
    } catch {
      setError("Could not find your waitlist entry.");
    }
  }, [id]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Register service worker and request push permission
  const enablePush = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('Notification' in window)) return;
      const reg = await navigator.serviceWorker.register('/sw-notifications.js');
      setSwReg(reg);
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setPushEnabled(true);
        // Listen for notification clicks
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data?.type === 'NOTIFICATION_CLICKED') {
            window.location.reload();
          }
        });
      }
    } catch { /* silent */ }
  };

  // WebSocket for real-time updates + push on table ready
  useEffect(() => {
    const wsUrl = API?.replace("https://", "wss://").replace("http://", "ws://");
    if (!wsUrl) return;
    let ws;
    try {
      ws = new WebSocket(`${wsUrl}/api/ws`);
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type === "waitlist_update") {
          fetchStatus();
        }
      };
    } catch { /* silent */ }
    return () => ws?.close();
  }, [fetchStatus]);

  // Send push notification when status changes to "notified"
  useEffect(() => {
    if (!item || !pushEnabled || !swReg) return;
    if (item.status === "notified") {
      swReg.active?.postMessage({
        type: 'SHOW_NOTIFICATION',
        title: 'Your table is ready!',
        body: `${item.guest_name}, please head to the host stand.`,
        tag: 'table-ready-' + item.id,
        url: window.location.href,
      });
    }
  }, [item?.status, pushEnabled, swReg, item]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#F9F9F7" }}>
        <div className="text-center">
          <XCircle size={48} className="mx-auto mb-3 text-stone-300" weight="duotone" />
          <p className="text-stone-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#F9F9F7" }}>
        <div className="text-center animate-pulse">
          <Clock size={48} className="mx-auto mb-3 text-stone-300" weight="duotone" />
          <p className="text-stone-500">Loading your status...</p>
        </div>
      </div>
    );
  }

  const cfg = STATUS_MAP[item.status] || STATUS_MAP.waiting;
  const Icon = cfg.icon;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#F9F9F7" }} data-testid="waitlist-status-page">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: "#2F4F4F", fontFamily: "Work Sans" }}>
            Dining Floor Studio
          </h1>
          <p className="text-xs text-stone-500 mt-1">Waitlist Status</p>
        </div>

        <div className="bg-white rounded-lg border border-stone-200 shadow-sm p-6 text-center">
          {/* Status Icon */}
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: cfg.color + "15" }}>
            <Icon size={32} weight="duotone" style={{ color: cfg.color }} />
          </div>

          {/* Guest Name */}
          <h2 className="text-xl font-semibold text-stone-800 mb-1" style={{ fontFamily: "Work Sans" }}>{item.guest_name}</h2>
          <Badge style={{ backgroundColor: cfg.color + "15", color: cfg.color, border: "none" }} className="text-sm px-3 py-1">
            {cfg.label}
          </Badge>

          {/* Details */}
          <div className="mt-6 space-y-3 text-sm text-stone-600">
            <div className="flex items-center justify-center gap-2">
              <Users size={16} weight="duotone" />
              <span>Party of {item.party_size}</span>
            </div>
            {item.position !== undefined && item.status === "waiting" && (
              <div className="flex items-center justify-center gap-2">
                <Clock size={16} weight="duotone" />
                <span>Position: <span className="font-bold text-stone-800">{item.position}</span> in line</span>
              </div>
            )}
            {item.estimated_wait && item.status === "waiting" && (
              <p className="text-xs text-stone-400">Estimated wait: ~{item.estimated_wait} minutes</p>
            )}
          </div>

          {/* Table Ready Message */}
          {item.status === "notified" && (
            <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: "#81B29A15" }}>
              <p className="text-sm font-medium" style={{ color: "#81B29A" }}>
                Your table is ready! Please head to the host stand.
              </p>
            </div>
          )}

          {/* Push Notification Button */}
          {!pushEnabled && item.status === "waiting" && (
            <button
              onClick={enablePush}
              className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-stone-200 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors"
              data-testid="enable-push-btn"
            >
              <Bell size={18} weight="duotone" style={{ color: "#2F4F4F" }} />
              Notify me when my table is ready
            </button>
          )}
          {pushEnabled && item.status === "waiting" && (
            <p className="mt-4 text-xs text-stone-400 flex items-center justify-center gap-1">
              <Bell size={14} weight="fill" style={{ color: "#81B29A" }} />
              You'll get a notification when your table is ready
            </p>
          )}
        </div>

        <p className="text-center text-[10px] text-stone-400 mt-4">
          This page auto-refreshes every 10 seconds
        </p>
      </div>
    </div>
  );
}
